import { format, parseISO } from 'date-fns'

import type { AppointmentResultReportCondition } from '@/api/types/ProviderApi'

/** What a visit's results amount to in a list: the score, and how much was found. */
export type VisitResults = {
	score: number
	/**
	 * How many findings the report holds.
	 *
	 * Kept beside `conditions` rather than derived from it, because a visit can be known to have
	 * scored without its report being reachable: `/appointment-results/latest` is the only source
	 * of findings, so every visit but the newest has a count and no text behind it.
	 */
	findings: number
	/** The findings themselves, where the app can reach them. */
	conditions?: AppointmentResultReportCondition[]
}

/**
 * One visit a member has had, as the Care list needs it.
 *
 * Nothing serves this yet. `/appointments/patient-upcoming` returns the future only, and
 * `/appointment-results/latest` returns one result with no date on it — so a member's own history
 * is unreachable from the app today. This is the shape the history endpoint should return: every
 * visit that has happened, with its results where they exist.
 */
export type VisitRecord = {
	appointmentId: number
	/** What the visit was: "Cleaning", "Perio Evaluation". What a member remembers it as. */
	name: string
	/** When it happened, ISO. */
	on: string
	/** Which studio, in its own name. */
	location: string
	/**
	 * The results, where they have been released.
	 *
	 * Three states, and they are not the same: results a member can read, results that exist but
	 * are not released yet, and a visit that was never going to produce any — a whitening consult,
	 * a follow-up. The last one has to say so, or the app looks like it lost the visit.
	 */
	results?: VisitResults
	/** The visit happened, a result is coming, nobody can read it yet. */
	resultsPending?: boolean
}

/** What a row can do: open its entries, say a result is coming, or say there was never one. */
export type VisitRowState = 'openable' | 'pending' | 'none'

export const rowStateOf = (visit: VisitRecord): VisitRowState =>
	visit.results ? 'openable' : visit.resultsPending ? 'pending' : 'none'

/**
 * The studio, without the brand on the front of it.
 *
 * The server calls them "Wally Tribeca", which is right on a map pin and redundant on the second
 * line of a row inside Wally's own app — and with a status pill beside it, the four characters it
 * costs are the difference between the line fitting and the line ending in an ellipsis.
 */
const studio = (location: string) => location.replace(/^Wally /, '')

/** "Sep 12, 2026 · Tribeca" — when and where, under the name of the thing itself. */
export const visitSubtitle = (visit: VisitRecord) =>
	`${format(parseISO(visit.on), 'MMM d, yyyy')} · ${studio(visit.location)}`

/**
 * Whether a visit's report can be opened, rather than merely summarised.
 *
 * A score and its findings are released together, so in practice a visit has both or neither.
 * Where the app holds a score and no report it is the app's gap, not the clinician's — and a
 * sheet whose only content is "not here yet" is a door onto an empty room. So the row states the
 * score and stops being a button.
 *
 * `conditions` present and empty is a real, openable report: it means the visit found nothing.
 */
export const canOpenReport = (visit: VisitRecord) => !!visit.results?.conditions

/** "Wally Score 85 · two findings" — what is inside the row, before opening it. */
export const resultsSummary = (results: VisitResults) => {
	const found =
		results.findings === 0
			? 'nothing found'
			: `${results.findings} ${results.findings === 1 ? 'finding' : 'findings'}`
	return `Wally Score ${results.score} · ${found}`
}

/** Newest first: a member reads their history from the visit they just had. */
export const byNewest = (a: VisitRecord, b: VisitRecord) =>
	Date.parse(b.on) - Date.parse(a.on)

/**
 * The note above the table, which is temporary by design.
 *
 * It leads with what the table can already do and names what is missing concretely, because a
 * member who has just been shown four visits will reasonably ask where the x-rays are. No date: the
 * one it carried was a promise to every member for as long as it stayed wrong. It lives in one
 * constant so that the day the records land, deleting the promise is deleting this.
 */
export const RECORDS_COMING =
	'Most visits include results and a Wally score. We are working hard on making your X-rays, scans and other data available as well.'

/**
 * How many visits the page carries before See All takes over.
 *
 * Three is a year and a half for a member on two cleanings a year: recent enough to be everything
 * they remember, short enough that Forms is still on the screen under it. The same trim Payments
 * makes on charges, for the same reason.
 */
export const VISITS_ON_PAGE = 3

/**
 * The visits by year, newest year first, each year's visits newest first.
 *
 * Payments groups its sheet by month, because a card statement is a monthly object. Visits are
 * annual furniture — two a year, at most — so a month grouping would be a list of headings with
 * one row under each.
 */
export const byYear = (visits: VisitRecord[]): [string, VisitRecord[]][] => {
	const years = new Map<string, VisitRecord[]>()
	for (const visit of [...visits].sort(byNewest)) {
		const year = format(parseISO(visit.on), 'yyyy')
		const found = years.get(year)
		if (found) found.push(visit)
		else years.set(year, [visit])
	}
	return [...years]
}

/** "Sep 12 · Tribeca" — inside a year group, the year is the heading's job. */
export const visitLine = (visit: VisitRecord) =>
	`${format(parseISO(visit.on), 'MMM d')} · ${studio(visit.location)}`

/**
 * What the score is, how it is worked out, and what it is for — in that order.
 *
 * True to the arithmetic in provider-kapi's `AppointmentResultsFacade`: a gum score and a tooth
 * score, fifty each, summed and capped at a hundred, less a deduction per detractor.
 *
 * Written to be said rather than read. Earlier drafts opened with the sum — "fifty points for your
 * gums, fifty for your teeth" — which is a spec sheet: nobody explains a number by reciting its
 * formula first. This starts where a person starts, with both halves full, and then says what
 * takes points away.
 *
 * Two things it is careful about. It does not say "findings": a report lists healthy conditions
 * too, and neither Healthy Teeth nor Neutral Saliva costs a point, so a member with two findings
 * and a ninety-four would have every reason to think the app was broken. And the deduction varies
 * with severity — three for gingivitis or a minor referral, five for a major one, ten for active
 * decay, fifteen for active gum disease — so "a few for something small, more for something
 * serious" describes a real scale rather than hedging. The figures themselves stay out: they live
 * in a service this copy cannot see, and a member who does the arithmetic and finds it does not
 * add up is worse off than one who never tried.
 *
 * It becomes the sum itself — fifty, fifty, minus what we found — the day `AppointmentResultReport`
 * carries `gumScore` and `toothScore`. Both are computed today and thrown away.
 */
export const SCORE_EXPLAINED =
	'Your gums and your teeth start on 50 points each. Then we take points off for anything that needs attention - a few for something small, more for something serious.'

/** The row that opens it, and what a visit with a clean report says instead of a list. */
export const SCORE_OPENER = 'How your score works'
export const NOTHING_FOUND = 'Nothing to flag'
export const NOTHING_FOUND_LINE =
	'Your clinician found nothing that needs watching at this visit.'
